import { yupResolver } from '@hookform/resolvers/yup';
import { DialogActions, DialogContent, Divider, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useAppTFunction } from '@retail/app/i18n';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { AssortmentTagItemsUpdate } from '@retail/my-assortment/context';
import { AssortmentItemMinimal, AssortmentTag } from '@retail/my-assortment/types';
import {
  dateRangeSchema,
  DateRangeSchema,
  EditAssortmentTagsTable,
  SharedTagDates,
} from '@retail/products/components';
import { getActiveAssortmentTags } from '@retail/products/utils';
import { getISO_8601Date } from '@retail/utils';
import { ContainedButton, OutlinedButton, TextButton } from '@shared/components';
import { useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';

interface Props {
  assortmentItems: AssortmentItemMinimal[];
  assortmentTags: AssortmentTag[];
  isSaving: boolean;
  continueToCustomerAssortments: boolean;
  onAddItems: (items: AssortmentItemMinimal[], tagUpdates: AssortmentTagItemsUpdate[]) => void;
  onClose: () => void;
  goBack: () => void;
}

export function HandleItemTags({
  assortmentItems,
  assortmentTags,
  isSaving,
  continueToCustomerAssortments,
  onAddItems,
  onClose,
  goBack,
}: Props) {
  const t = useAppTFunction();
  const apiRef = useGridApiRef();
  const { isBrandContext } = useSelectedOrgUnit();

  const { control, handleSubmit } = useForm<DateRangeSchema>({
    resolver: yupResolver(dateRangeSchema(t)),
    mode: 'onChange',
    defaultValues: { fromDate: new Date(), toDate: null },
  });

  const [fromDate, toDate] = useWatch({ control, name: ['fromDate', 'toDate'] });

  const onSubmit = useCallback(
    ({ fromDate: sharedFrom, toDate: sharedTo }: DateRangeSchema) => {
      const taggedItems = apiRef.current
        ? (Array.from(apiRef.current.getRowModels().values()) as AssortmentItemMinimal[])
        : [];

      const validFrom = getISO_8601Date(isBrandContext ? sharedFrom : new Date());
      const validToDate = isBrandContext ? sharedTo : null;
      const validTo = validToDate ? getISO_8601Date(validToDate) : undefined;

      const supplierItemIdsByTagId = new Map<number, number[]>();
      taggedItems.forEach((item) =>
        getActiveAssortmentTags(item.assortmentTags).forEach(({ tagId }) =>
          supplierItemIdsByTagId.set(tagId, [
            ...(supplierItemIdsByTagId.get(tagId) ?? []),
            item.mgSupplierItemId,
          ])
        )
      );

      onAddItems(
        taggedItems,
        Array.from(supplierItemIdsByTagId, ([tagId, supplierItemIds]) => ({
          tagId,
          supplierItemIds,
          validFrom,
          validTo,
        }))
      );
    },
    [apiRef, isBrandContext, onAddItems]
  );

  return (
    <>
      <DialogContent
        sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}
        className="padding-0"
      >
        <EditAssortmentTagsTable
          apiRef={apiRef}
          assortmentItems={assortmentItems}
          assortmentTags={assortmentTags}
          validFrom={isBrandContext ? fromDate : undefined}
          validTo={isBrandContext ? toDate ?? undefined : undefined}
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: isBrandContext ? 'space-between' : 'flex-end' }}>
        {isBrandContext && <SharedTagDates control={control} />}
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextButton onClick={onClose}>{t('products.cancel')}</TextButton>

          <OutlinedButton color="secondary" size="small" onClick={goBack}>
            {t('common.goBack')}
          </OutlinedButton>

          <ContainedButton
            color="secondary"
            size="small"
            loading={isSaving}
            onClick={handleSubmit(onSubmit)}
          >
            {continueToCustomerAssortments
              ? t('myAssortment.actions.continueToCustomerAssortments')
              : t('myAssortment.actions.addItems')}
          </ContainedButton>
        </Stack>
      </DialogActions>
    </>
  );
}
